import { moneyToDecimalString } from '@ibe/utils';

import { type DailyRate, type Tax } from '../api/schemas.js';

export interface RateTableProps {
  readonly rates: readonly DailyRate[];
  readonly currency: string;
  readonly taxes: readonly Tax[];
}

function formatDate(value: string): string {
  const at = new Date(`${value}T00:00:00Z`);
  return at.toLocaleDateString('en-AU', {
    timeZone: 'UTC',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    weekday: 'short',
  });
}

function formatMoney(value: string, currencyCode: string): string {
  try {
    const decimal = moneyToDecimalString({ amountMinor: BigInt(value), currency: currencyCode });
    return `${currencyCode} ${decimal}`;
  } catch {
    return value;
  }
}

export function RateTable({ rates, currency, taxes }: RateTableProps): React.JSX.Element {
  const taxById = new Map(taxes.map((tax) => [tax.id, tax]));

  return (
    <table className="w-full caption-bottom text-sm">
      <caption className="sr-only">Daily rates</caption>
      <thead className="border-b bg-surface">
        <tr>
          <th scope="col" className="h-11 px-3 text-left text-sm font-medium text-text-muted">
            Date
          </th>
          <th scope="col" className="h-11 px-3 text-left text-sm font-medium text-text-muted">
            Base rate
          </th>
          <th scope="col" className="h-11 px-3 text-left text-sm font-medium text-text-muted">
            Tax applied
          </th>
          <th scope="col" className="h-11 px-3 text-left text-sm font-medium text-text-muted">
            Tax type
          </th>
          <th scope="col" className="h-11 px-3 text-left text-sm font-medium text-text-muted">
            Status
          </th>
        </tr>
      </thead>
      <tbody>
        {rates.map((rate) => {
          const tax = rate.taxId === null ? undefined : taxById.get(rate.taxId);
          return (
            <tr key={rate.date} className="border-b last:border-0 hover:bg-surface-muted/70">
              <td className="px-3 py-3.5 align-middle text-sm text-text">
                {formatDate(rate.date)}
              </td>
              <td className="px-3 py-3.5 align-middle text-sm text-text">
                {formatMoney(rate.baseRate, currency)}
              </td>
              <td className="px-3 py-3.5 align-middle text-sm text-text">
                {rate.taxApplied ? 'Yes' : 'No'}
              </td>
              <td className="px-3 py-3.5 align-middle text-sm text-text">
                {tax === undefined ? '—' : tax.name}
              </td>
              <td className="px-3 py-3.5 align-middle text-sm">
                {rate.closed ? (
                  <span className="text-rose-600">Closed</span>
                ) : (
                  <span className="text-emerald-600">Open</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
